import type { OrderRatingResolvedBy } from 'api/orderRatings.types'
import type { TimelineItem } from 'components/Timeline'
import { buildTranslate } from 'locales'
import { resolutionStatusFor } from './resolutionStatus'

type Translate = ReturnType<typeof buildTranslate>

// The Case timeline card's 2-step stepper (mockup: submit → resolution
// state). An OrderRating carries no backend timeline field, so this
// synthesizes it client-side from the two facts the record does have — when
// the complaint landed, and who (if anyone) has since resolved it.
export const synthesizeTimeline = (
  rating: { date: string | null; resolvedBy: OrderRatingResolvedBy },
  formatTime: (iso: string | null) => string,
  t: Translate
): TimelineItem[] => {
  const submitted: TimelineItem = {
    id: 'submitted',
    time: formatTime(rating.date),
    description: t('timeline.step_submitted'),
  }

  // The shared resolution → badge vocabulary (resolutionStatusFor), branched
  // onto this card's three steps. An operator's close is NEUTRAL — the wire
  // can't tell a dismissal from a real resolution, so the step mirrors the
  // header's closed_by_operator badge rather than asserting an outcome we can't
  // verify. Only the AI reads as a genuine "Resolved" step.
  const status = resolutionStatusFor(rating.resolvedBy.kind)

  if (status === 'unresolved') {
    return [
      submitted,
      {
        id: 'awaiting',
        time: t('timeline.step_awaiting.title'),
        description: t('timeline.step_awaiting.description'),
      },
    ]
  }

  if (status === 'resolved') {
    return [
      submitted,
      {
        id: 'resolved',
        time: t('timeline.step_resolved.title'),
        description: t('timeline.step_resolved.description', {
          resolver: t('resolution.resolved_by_ai'),
        }),
      },
    ]
  }

  const closerName = rating.resolvedBy.name
  return [
    submitted,
    {
      id: 'closed',
      time: t('timeline.step_closed.title'),
      description: closerName
        ? t('timeline.step_closed.description', { resolver: closerName })
        : t('timeline.step_closed.description_no_name'),
    },
  ]
}
